import { Address } from '@/models/common/address';
import { resolve } from 'path';

/// Based on https://markus.oberlehner.net/blog/using-the-google-maps-api-with-vue/
export default class GMaps {
    private callbackName: string = 'gmapsCallback';

    private initialized = !!(window as any).google;
    private resolveInitPromise: any;
    private rejectInitPromise: any;

    private initPromise = new Promise((resolve, reject) => {
        this.resolveInitPromise = resolve;
        this.rejectInitPromise = reject;
    });

    public init(): Promise<any> {
        // If Google Maps already is initialized
        // the `initPromise` should get resolved
        // eventually.
        if (this.initialized) {
            return this.initPromise;
        }

        this.initialized = true;
        // The callback function is called by
        // the Google Maps script if it is
        // successfully loaded.
        (window as any)[this.callbackName] = () => this.resolveInitPromise((window as any).google);

        // We inject a new script tag into
        // the `<head>` of our HTML to load
        // the Google Maps script.
        const script = document.createElement('script');
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_CLOUD_PLATFORM_API_KEY}&callback=${this.callbackName}`;
        script.onerror = this.rejectInitPromise;
        document.querySelector('head')!.appendChild(script);

        return this.initPromise;
    }

    public async setAddressLocation(address: Address): Promise<any> {
        const addressToLocate = address.getInOneLine();

        if (addressToLocate) {
            const google = await this.init();

            return new Promise<any>((resolve) => {
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode(
                    { address: addressToLocate },
                    (results: any, status: any) => {
                        if (status !== 'OK' || !results[0]) {
                            throw new Error(status);
                        }

                        address.setLocation(results[0].geometry.location.lat(), results[0].geometry.location.lng());

                        resolve(true);
                    },
                );
            });
        }

        return new Promise<any>((resolve) => {
            resolve(false);
        });
    }

    public getUrlOfAddress(address: Address): string {
        const addressToQuery = address.getInOneLine();

        if (addressToQuery) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURI(addressToQuery)}`;
        }

        return '';
    }
}

export const gmaps = new GMaps();
